import React, { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../auth/AuthContext';
import apiClient from '../api/client';
import './ConferenciaRodada.css';

/**
 * Página Admin - Conferência da Rodada
 * Checkpoint de consulta (somente leitura): visão única com saldo devedor,
 * posição final, prêmio e saldo final de cada jogador, com base no estado
 * atual da rodada - útil pra conferir tudo antes de fechar a rodada
 * oficialmente. Inclui a conferência cruzando nocautes recebidos x rebuys
 * lançados.
 */
const ConferenciaRodada = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { isAdmin } = useAuth();

  const [dados, setDados] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [expandidos, setExpandidos] = useState(new Set());

  const formatarData = (data) => {
    if (!data) return '-';
    return new Date(data).toLocaleDateString('pt-BR', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  const formatarMoeda = (valor) => `R$ ${parseFloat(valor || 0).toFixed(2)}`;

  const carregarDados = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      const response = await apiClient.get(`/rodadas/${id}/conferencia`);
      setDados(response.data);
    } catch (err) {
      console.error('Erro ao carregar conferência da rodada:', err);
      setError(err.response?.data?.error || 'Erro ao carregar conferência da rodada');
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    if (!isAdmin) {
      navigate('/dashboard');
      return;
    }
    carregarDados();
  }, [isAdmin, navigate, carregarDados]);

  const alternarDetalhes = (jogadorId) => {
    setExpandidos((atual) => {
      const novo = new Set(atual);
      if (novo.has(jogadorId)) {
        novo.delete(jogadorId);
      } else {
        novo.add(jogadorId);
      }
      return novo;
    });
  };

  const statusLabel = {
    ok: '✓ OK',
    atencao: '⚠ Atenção',
    divergente: '⚠ Divergente',
    erro_grave: '✗ Erro grave',
  };

  if (loading) {
    return <div className="container">Carregando...</div>;
  }

  return (
    <div className="container conferencia-rodada-container">
      <div className="page-header conferencia-rodada-header">
        <div>
          <h1>🔍 Conferência da Rodada</h1>
          <p>Saldo devedor, posição final, prêmio e saldo final de cada jogador, com conferência de nocautes x rebuys.</p>
        </div>
        <button className="btn btn-secondary" onClick={() => navigate('/admin/menu')}>
          ← Voltar
        </button>
      </div>

      {error && <div className="error">{error}</div>}

      {dados && (
        <>
          <div className="resumo-card">
            <h2>Rodada de {formatarData(dados.rodada.data)}</h2>
            <div className="resumo-grid">
              <div>
                <strong>Status:</strong>{' '}
                <span className={`status-badge ${dados.rodada.status}`}>
                  {dados.rodada.status === 'aberta' ? '🟢 Aberta' : '🔴 Fechada'}
                </span>
              </div>
              <div>
                <strong>Participantes:</strong> {dados.resumo.total_participantes}
              </div>
              <div>
                <strong>Total devido geral:</strong> {formatarMoeda(dados.resumo.total_devido_geral)}
              </div>
              <div>
                <strong>Base premiável:</strong> {formatarMoeda(dados.resumo.valor_base_premiavel)}
              </div>
              <div>
                <strong>Premiação total:</strong> {formatarMoeda(dados.resumo.premiacao_total)}
              </div>
              <div>
                <strong>Caixinha estimada ({dados.resumo.percentual_caixinha}%):</strong>{' '}
                {formatarMoeda(dados.resumo.caixinha_estimada)}
              </div>
              {dados.resumo.posicoes_pendentes > 0 && (
                <div>
                  <strong className="destaque-aviso">Posições pendentes:</strong>{' '}
                  <span className="destaque-aviso">{dados.resumo.posicoes_pendentes}</span>
                </div>
              )}
              <div>
                <strong>Divergências:</strong>{' '}
                <span className={dados.resumo.qtd_divergencias > 0 ? 'destaque-alerta' : ''}>
                  {dados.resumo.qtd_divergencias}
                </span>
              </div>
              <div>
                <strong>Avisos:</strong>{' '}
                <span className={dados.resumo.qtd_avisos > 0 ? 'destaque-aviso' : ''}>
                  {dados.resumo.qtd_avisos}
                </span>
              </div>
              <div>
                <strong>Erros graves:</strong>{' '}
                <span className={dados.resumo.qtd_erros_graves > 0 ? 'destaque-erro' : ''}>
                  {dados.resumo.qtd_erros_graves}
                </span>
              </div>
            </div>
          </div>

          <div className="conferencia-tabela-card">
            {dados.participantes.length === 0 ? (
              <p className="no-data">Nenhum participante lançado nesta rodada ainda.</p>
            ) : (
              <div className="table-scroll">
                <table className="table-resultados">
                  <thead>
                    <tr>
                      <th>Jogador</th>
                      <th>Posição final</th>
                      <th>Saldo devedor</th>
                      <th>Prêmio</th>
                      <th>Saldo final</th>
                      <th>Conferência de nocautes</th>
                      <th></th>
                    </tr>
                  </thead>
                  <tbody>
                    {dados.participantes.map((p) => {
                      const expandido = expandidos.has(p.jogador_id);
                      return (
                        <React.Fragment key={p.jogador_id}>
                          <tr className={`linha-status-${p.conferencia_nocautes.status.replace('_', '-')}`}>
                            <td>{p.jogador_nome}</td>
                            <td>{p.posicao_final ? `${p.posicao_final}º` : <span className="observacao-pendente">Pendente</span>}</td>
                            <td className="total-devedor-celula">{formatarMoeda(p.debitos.total_devedor)}</td>
                            <td>{p.premio !== null ? formatarMoeda(p.premio) : <span className="observacao-pendente">—</span>}</td>
                            <td className={p.saldo_final === null ? '' : (p.saldo_final >= 0 ? 'saldo-positivo' : 'saldo-negativo')}>
                              {p.saldo_final !== null ? formatarMoeda(p.saldo_final) : <span className="observacao-pendente">—</span>}
                            </td>
                            <td className="conferencia-celula">
                              <div className={`conferencia-badge status-${p.conferencia_nocautes.status.replace('_', '-')}`}>
                                {statusLabel[p.conferencia_nocautes.status] || p.conferencia_nocautes.status}
                              </div>
                              <div className="conferencia-detalhe">
                                {p.conferencia_nocautes.recebidos} nocaute(s) · {p.conferencia_nocautes.eliminacoes} eliminação(ões) · {p.conferencia_nocautes.rebuys_lancados} rebuy(s)
                              </div>
                              {p.conferencia_nocautes.status !== 'ok' && (
                                <div className="conferencia-mensagem">{p.conferencia_nocautes.mensagem}</div>
                              )}
                            </td>
                            <td>
                              <button
                                type="button"
                                className="btn-detalhes"
                                onClick={() => alternarDetalhes(p.jogador_id)}
                              >
                                {expandido ? '▲ Ocultar' : '▼ Detalhes'}
                              </button>
                            </td>
                          </tr>
                          {expandido && (
                            <tr className="linha-detalhes">
                              <td colSpan={7}>
                                <div className="detalhes-devedor-grid">
                                  <div><strong>Entrada:</strong> {formatarMoeda(p.debitos.entrada)}</div>
                                  <div><strong>Buy-in inicial:</strong> {formatarMoeda(p.debitos.buyin_inicial)}</div>
                                  <div><strong>Rebuys simples:</strong> {formatarMoeda(p.debitos.rebuys_simples)}</div>
                                  <div><strong>Rebuys duplo:</strong> {formatarMoeda(p.debitos.rebuys_duplo)}</div>
                                  <div><strong>Add-on:</strong> {formatarMoeda(p.debitos.addon)}</div>
                                  <div><strong>Jackpot:</strong> {formatarMoeda(p.debitos.jackpot)}</div>
                                  <div><strong>Janta:</strong> {formatarMoeda(p.debitos.janta)}</div>
                                </div>
                              </td>
                            </tr>
                          )}
                        </React.Fragment>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
};

export default ConferenciaRodada;
